import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import { Lock, ShieldCheck, MessageCircle, Wallet, Coins, Loader2, IndianRupee, Check } from "lucide-react";

/**
 * SecureConnectModal — pay ₹199 to unlock a private 7-day chat with another educator.
 *
 *  Props:
 *    open: boolean
 *    onClose: () => void
 *    recipientUserId: string
 *    recipientName?: string   (for the modal preview if /quote hasn't responded yet)
 *
 *  Behavior:
 *   - On open, calls /api/connect/quote to fetch cost + wallet balance.
 *   - If an active connect already exists → "Open chat" button (instant route).
 *   - Else 2 buttons:
 *       (a) Pay 199 coins  — disabled if balance < 199, with link to /wallet
 *       (b) Pay ₹199 via Razorpay  — direct INR path (mocked until live)
 *   - On success, navigate to /inbox/{connect_id}.
 */
export default function SecureConnectModal({ open, onClose, recipientUserId, recipientName }) {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKind, setBusyKind] = useState(null);   // "coins" | "razorpay" | null

  // eslint-disable-next-line react-hooks/set-state-in-effect -- onClose calls setQuote(null) via parent unmount; the inner .catch is in promise context, not React render
  useEffect(() => {
    if (!open || !recipientUserId) return;
    setLoading(true);
    api.post("/connect/quote", { recipient_user_id: recipientUserId })
      .then(({ data }) => setQuote(data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not load Secure Connect quote.");
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [open, recipientUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const payWithCoins = async () => {
    if (!quote) return;
    setBusyKind("coins");
    try {
      const { data } = await api.post("/connect/start", { recipient_user_id: recipientUserId });
      toast.success("Connection unlocked · 7 days of secure chat");
      onClose?.();
      navigate(`/inbox/${data.connect_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, {
          action: { label: "Recharge", onClick: () => navigate("/wallet") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not unlock connection.");
      }
    } finally { setBusyKind(null); }
  };

  const payWithRazorpay = async () => {
    setBusyKind("razorpay");
    try {
      const { data } = await api.post("/connect/razorpay/checkout", { recipient_user_id: recipientUserId });
      toast.success(`Paid ₹${data.amount_inr || 199} · Connection unlocked`, {
        description: data.mocked ? "Payment processed (test mode)" : "Payment processed",
      });
      onClose?.();
      navigate(`/inbox/${data.connect_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Payment could not be processed.");
    } finally { setBusyKind(null); }
  };

  const openExisting = () => {
    if (!quote?.existing_connect_id) return;
    onClose?.();
    navigate(`/inbox/${quote.existing_connect_id}`);
  };

  const rec = quote?.recipient || { display_name: recipientName };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="secure-connect-modal">
        <div className="relative bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-500 text-white px-6 pt-6 pb-5">
          <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/15 blur-2xl pointer-events-none" />
          <DialogHeader className="text-left relative">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold w-fit">
              <Lock className="h-3 w-3" /> Secure Connect
            </span>
            <DialogTitle className="font-display text-2xl tracking-tight font-medium mt-3">
              Unlock a private chat
            </DialogTitle>
            <DialogDescription className="text-white/85 text-sm mt-1">
              Pay once · talk for <strong>7 days</strong> · unlimited messages, files, images & voice.
              Only the two of you can read it.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-4">
          {loading || !quote ? (
            <>
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
              <Skeleton className="h-10" />
            </>
          ) : (
            <>
              {/* Recipient card */}
              <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3">
                {rec?.photo_url ? (
                  <img src={rec.photo_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <span className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-400 to-amber-300 grid place-items-center text-white font-medium">
                    {(rec?.display_name || "?").charAt(0).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm leading-tight truncate" data-testid="connect-recipient-name">
                    {rec?.display_name || "Educator"}
                  </p>
                  {rec?.headline && (
                    <p className="text-xs text-muted-foreground truncate">{rec.headline}</p>
                  )}
                </div>
              </div>

              {/* If already active, offer "Open chat" */}
              {quote.existing_connect_id && quote.existing_status === "active" ? (
                <div className="space-y-3" data-testid="connect-already-active">
                  <p className="text-sm text-emerald-600 dark:text-emerald-300 inline-flex items-center gap-1.5">
                    <Check className="h-4 w-4" /> You already have an active connection
                  </p>
                  <Button onClick={openExisting} className="w-full h-11 rounded-full" data-testid="connect-open-existing">
                    <MessageCircle className="h-4 w-4 mr-1.5" /> Open chat
                  </Button>
                </div>
              ) : (
                <>
                  {/* What you get */}
                  <ul className="text-xs text-muted-foreground space-y-1.5">
                    <Bullet>Private — only the two of you can read messages</Bullet>
                    <Bullet>Unlimited text · images · files · voice notes</Bullet>
                    <Bullet>7 days of access for <strong>₹{quote.cost_inr}</strong> (or <strong>{quote.cost_coins} TeeBee Coins</strong>)</Bullet>
                    <Bullet>No subscription — pay once</Bullet>
                  </ul>

                  {/* Pay options */}
                  <div className="space-y-2 pt-1" data-testid="connect-pay-options">
                    {/* Coins option */}
                    <Button
                      onClick={payWithCoins}
                      disabled={!quote.can_pay_with_coins || busyKind !== null}
                      variant={quote.can_pay_with_coins ? "default" : "outline"}
                      className="w-full h-12 rounded-full justify-between px-5"
                      data-testid="connect-pay-coins"
                    >
                      <span className="inline-flex items-center gap-2">
                        {busyKind === "coins" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                        Pay {quote.cost_coins} TeeBee Coins
                      </span>
                      <span className="text-xs opacity-80">
                        Balance: <strong>{quote.user_balance}</strong>
                      </span>
                    </Button>
                    {!quote.can_pay_with_coins && (
                      <button
                        onClick={() => { onClose?.(); navigate("/wallet"); }}
                        className="block text-xs text-primary hover:underline"
                        data-testid="connect-add-coins"
                      >
                        <Wallet className="inline h-3 w-3 mr-1" />
                        Need {quote.cost_coins - quote.user_balance} more coins · Add coins
                      </button>
                    )}

                    {/* Razorpay option */}
                    <Button
                      onClick={payWithRazorpay}
                      disabled={busyKind !== null}
                      variant="outline"
                      className="w-full h-12 rounded-full justify-between px-5"
                      data-testid="connect-pay-razorpay"
                    >
                      <span className="inline-flex items-center gap-2">
                        {busyKind === "razorpay" ? <Loader2 className="h-4 w-4 animate-spin" /> : <IndianRupee className="h-4 w-4" />}
                        Pay ₹{quote.cost_inr} directly
                      </span>
                      <span className="text-xs opacity-70">Razorpay</span>
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-6 pb-5 pt-0 sm:flex-col sm:space-x-0 gap-1">
          <p className="text-[10px] text-muted-foreground inline-flex items-center gap-1.5 justify-center w-full">
            <ShieldCheck className="h-3 w-3" />
            Encrypted at rest · only the two parties can read · no third-party access
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
